import { Button } from "@/shared/components/ui/button";
import { ArrowLeft, ChevronRight, Home } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { getBreadcrumbs } from "@/shared/lib/breadcrumbs";

interface PageHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  backTo?: string;
  children?: React.ReactNode;
}

const PageHeader = ({ title, subtitle, backTo, children }: PageHeaderProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const breadcrumbs = getBreadcrumbs(pathname);

  // Don't show back arrow on the main dashboard
  const isRootDashboard = pathname === "/dashboard";

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="pb-5 mb-6 border-b border-[rgba(169,171,174,0.25)]">
      {breadcrumbs.length > 1 && (
        <nav className="flex items-center gap-1 text-xs text-[#a9abae] flex-wrap mb-2">
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />}
              {i === 0 && <Home className="h-3 w-3 shrink-0 mr-0.5 opacity-70" />}
              {crumb.path ? (
                <Link
                  to={crumb.path}
                  className="hover:text-[#791614] transition-colors whitespace-nowrap"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className="text-[#374151] font-medium whitespace-nowrap">{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {!isRootDashboard && (
            <Button
              variant="ghost"
              size="icon"
              className="mt-0.5 shrink-0 text-[#a9abae] hover:text-[#791614] transition-colors"
              onClick={handleBack}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <div className="min-w-0">
            <h1 className="font-display text-xl sm:text-2xl font-semibold text-[#0f172a] leading-tight break-words">{title}</h1>
            {subtitle && <div className="text-sm text-[#6b7280] mt-1">{subtitle}</div>}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

export default PageHeader;
